/**
 * Payment methods screen (DNKADM-0091, §E / DNKAPI-0171 **[COMING]**) — the
 * tenant's managed list of payment methods + add/rename/reorder/activate-
 * deactivate/delete. Lives as the **Payment methods** tab of Credit Card Settings
 * (`/connections/payment-methods`) — co-located with payment connections because
 * `card` methods derive from those connections. `RequireAdmin`-gated by the route.
 *
 * Behavior (handoff §E):
 *  - **Cash** is PROTECTED — its Delete is disabled (rename / reorder /
 *    activate-deactivate still allowed); a `400` on any protected delete is
 *    surfaced as a toast, never a crash.
 *  - **card** methods (from connections) are read-mostly — shown, renamable and
 *    reorderable, but offered NO destructive delete (their existence is driven by
 *    the connection).
 *  - **external** methods get the full CRUD, including delete.
 *  - A duplicate label on add/rename surfaces inline on the dialog ("That label is
 *    taken.").
 *
 * **Forward-compatible:** if the endpoint isn't live yet (`404`), the screen shows
 * a NEUTRAL "Payment methods aren't available yet" state (not an error), and the
 * Add-Member pick-list (`usePaymentMethodPickList`) degrades to Cash-only.
 *
 * **Toolbar, sort and URL (DNKADM-0430 P1-F).** The shared {@link ListToolbar} holds a
 * name search; Order, Name, Type and Status have shared {@link SortableHeader}s. Order is
 * the display order (`sortOrder`) and stays the default. A column sort is a view only: the
 * move up/down actions (the one write to `sortOrder`) are enabled only in the default
 * order with no search, and a note says so otherwise. Search and sort live in the URL
 * (`paymentMethodsListView.ts`).
 */
import { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  IconButton,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { IconAction } from '@/components/IconAction';
import { ListNoMatch } from '@/components/list/ListNoMatch';
import { ListToolbar } from '@/components/list/ListToolbar';
import { SortableHeader } from '@/components/list/SortableHeader';
import { parseSort, sortRows, toggleSort, toSortParams } from '@/components/list/listSort';
import { useListUrlState } from '@/components/list/useListUrlState';
import { Icon } from '@/theme';
import { ApiError } from '@/api/errors';
import { useResource } from '@/hooks/useResource';
import { useToast } from '@/components/useToast';
import { serverMessage } from '@/api/serverMessage';
import {
  deletePaymentMethod,
  listPaymentMethods,
  updatePaymentMethod,
  type PaymentMethod,
} from '@/api/paymentMethods';
import { PaymentMethodDialog } from './PaymentMethodDialog';
import {
  KIND_LABEL,
  PAYMENT_METHOD_DEFAULT_SORT,
  PAYMENT_METHOD_SORT_KEYS,
  PAYMENT_METHODS_URL_DEFAULTS,
  bySortOrder,
  paymentMethodMatches,
  paymentMethodSortValue,
  type PaymentMethodSortKey,
} from './paymentMethodsListView';
import { ScreenError } from '@/components/ScreenError';

export function PaymentMethodsScreen() {
  const showToast = useToast();
  // DNKADM-0430: search and sort come from the URL (`paymentMethodsListView.ts`).
  const url = useListUrlState(PAYMENT_METHODS_URL_DEFAULTS);
  const setUrl = url.set;
  const search = url.values.q;
  const sort = parseSort(
    url.values.sort,
    url.values.dir,
    PAYMENT_METHOD_SORT_KEYS,
    PAYMENT_METHOD_DEFAULT_SORT,
  );

  // Load ALL methods (active + inactive) so the admin can manage the full list and
  // reactivate deactivated ones (§E supports status=active|all).
  const methods = useResource(useCallback(() => listPaymentMethods('all'), []));

  // Dialog state: `null` = closed; `{ method: null }` = add; `{ method }` = rename.
  const [dialog, setDialog] = useState<{ method: PaymentMethod | null } | null>(null);

  const refetch = methods.refetch;

  // The display order (`sortOrder`) — what the dropdowns show and what reorder edits.
  const sorted = useMemo(
    () => (methods.data ? [...methods.data].sort(bySortOrder) : []),
    [methods.data],
  );
  // Each method's 1-based place in the display order (the Order column).
  const positions = useMemo(
    () => new Map(sorted.map((method, index) => [method.id, index + 1])),
    [sorted],
  );
  // The rows in view: searched, then column-sorted. A view only — never a `sortOrder` write.
  const visibleRows = useMemo(
    () =>
      sortRows(
        sorted.filter((method) => paymentMethodMatches(method, search)),
        (method) => paymentMethodSortValue(method, sort.key, positions.get(method.id) ?? 0),
        sort.dir,
        (method) => positions.get(method.id) ?? 0,
      ),
    [sorted, positions, search, sort.key, sort.dir],
  );
  const onSort = useCallback(
    (key: PaymentMethodSortKey) =>
      setUrl(toSortParams(toggleSort(sort, key), PAYMENT_METHOD_DEFAULT_SORT)),
    [setUrl, sort],
  );
  // Reordering is offered only when the rows on screen ARE the display order.
  const canReorder =
    sort.key === PAYMENT_METHOD_DEFAULT_SORT.key &&
    sort.dir === PAYMENT_METHOD_DEFAULT_SORT.dir &&
    search.trim().length === 0;

  const handleToggleActive = useCallback(
    async (method: PaymentMethod) => {
      try {
        await updatePaymentMethod(method.id, { active: !method.active });
        refetch();
        showToast(
          method.active ? 'Payment method deactivated' : 'Payment method reactivated',
          'success',
        );
      } catch (err) {
        showToast(serverMessage(err) ?? 'Couldn’t update the payment method.', 'error');
      }
    },
    [refetch, showToast],
  );

  // Reorder by swapping this method's sortOrder with its display neighbor's, then
  // refetch (two PUTs, each `{ sortOrder }`). A no-op at the list ends.
  const handleReorder = useCallback(
    async (method: PaymentMethod, direction: 'up' | 'down') => {
      const index = sorted.findIndex((m) => m.id === method.id);
      const neighbor = sorted[direction === 'up' ? index - 1 : index + 1];
      if (!neighbor) return;
      try {
        await Promise.all([
          updatePaymentMethod(method.id, { sortOrder: neighbor.sortOrder }),
          updatePaymentMethod(neighbor.id, { sortOrder: method.sortOrder }),
        ]);
        refetch();
      } catch (err) {
        showToast(serverMessage(err) ?? 'Couldn’t reorder the payment methods.', 'error');
      }
    },
    [sorted, refetch, showToast],
  );

  const handleDelete = useCallback(
    async (method: PaymentMethod) => {
      try {
        await deletePaymentMethod(method.id);
        refetch();
        showToast('Payment method removed', 'success');
      } catch (err) {
        // A protected (Cash) delete may 400 — surface gracefully, never crash.
        showToast(serverMessage(err) ?? 'This payment method can’t be removed.', 'error');
      }
    },
    [refetch, showToast],
  );

  // Forward-compat: a 404 means the endpoint isn't live yet — a NEUTRAL state, not
  // an error (DNKAPI-0171 [COMING]).
  const notAvailableYet =
    methods.status === 'error' &&
    methods.error instanceof ApiError &&
    methods.error.code === 'NOT_FOUND';

  return (
    <Box>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          mb: 2,
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h6" component="h2">
            Manual Payment methods
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary', maxWidth: 620 }}>
            The methods staff can record when adding a member. Cash is always available; card
            methods come from your payment connections; add your own (like Venmo or PayPal).
          </Typography>
        </Box>
        {methods.status === 'ready' && (
          <Button
            variant="contained"
            startIcon={<Icon name="add" />}
            onClick={() => setDialog({ method: null })}
          >
            Add method
          </Button>
        )}
      </Stack>

      {methods.status === 'loading' && (
        <Skeleton
          variant="rounded"
          height={220}
          role="status"
          aria-label="Loading payment methods"
          sx={{ borderRadius: 3 }}
        />
      )}

      {notAvailableYet && (
        <Alert severity="info" sx={{ maxWidth: 620 }}>
          <AlertTitle sx={{ fontWeight: 500 }}>Manual payment methods aren’t available yet</AlertTitle>
          This feature is coming soon. Until then, Cash is used for member payments.
        </Alert>
      )}

      {methods.status === 'error' && !notAvailableYet && (
        <ScreenError
          error={methods.error}
          what="the payment methods"
          onRetry={methods.refetch}
          sx={{ alignItems: 'center' }}
        />
      )}

      {methods.status === 'ready' && sorted.length > 0 && (
        <ListToolbar
          search={{
            value: search,
            onChange: (next) => setUrl({ q: next }),
            placeholder: 'Search payment methods',
          }}
          onClear={() => setUrl({ q: null })}
          end={
            canReorder ? null : (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                To reorder, sort by Order and clear the search.
              </Typography>
            )
          }
        />
      )}

      {methods.status === 'ready' && sorted.length === 0 && (
        <Alert severity="info" sx={{ maxWidth: 620 }}>
          No payment methods yet. Add your first custom method, or set up a card connection on the
          Connection tab.
        </Alert>
      )}

      {methods.status === 'ready' && sorted.length > 0 && (
        <TableContainer>
          <Table aria-label="Manual Payment methods" size="small">
            <TableHead>
              <TableRow>
                <SortableHeader
                  label="Order"
                  col="order"
                  align="right"
                  sort={sort}
                  onSort={onSort}
                />
                <SortableHeader label="Name" col="name" sort={sort} onSort={onSort} />
                <SortableHeader label="Type" col="type" sort={sort} onSort={onSort} />
                <SortableHeader label="Status" col="status" sort={sort} onSort={onSort} />
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((method) => {
                const isCash = method.kind === 'cash';
                const isCard = method.kind === 'card';
                // The place in the display order, which the move actions step through.
                const position = positions.get(method.id) ?? 0;
                return (
                  <TableRow key={method.id}>
                    <TableCell align="right" sx={{ color: 'text.secondary', width: 72 }}>
                      {position}
                    </TableCell>
                    <TableCell>{method.label}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={KIND_LABEL[method.kind]}
                        variant="outlined"
                        color={isCard ? 'primary' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={method.active ? 'Active' : 'Inactive'}
                        color={method.active ? 'success' : 'default'}
                        variant={method.active ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconAction
                        size="small"
                        label={`Move ${method.label} up`}
                        disabled={!canReorder || position === 1}
                        onClick={() => void handleReorder(method, 'up')}
                      >
                        <Icon name="arrow_upward" />
                      </IconAction>
                      <IconAction
                        size="small"
                        label={`Move ${method.label} down`}
                        disabled={!canReorder || position === sorted.length}
                        onClick={() => void handleReorder(method, 'down')}
                      >
                        <Icon name="arrow_downward" />
                      </IconAction>
                      <IconAction
                        size="small"
                        label={`Rename ${method.label}`}
                        onClick={() => setDialog({ method })}
                      >
                        <Icon name="edit" />
                      </IconAction>
                      {method.active ? (
                        <IconAction
                          size="small"
                          label={`Deactivate ${method.label}`}
                          onClick={() => void handleToggleActive(method)}
                        >
                          <Icon name="toggle_off" />
                        </IconAction>
                      ) : (
                        <IconAction
                          size="small"
                          label={`Reactivate ${method.label}`}
                          onClick={() => void handleToggleActive(method)}
                        >
                          <Icon name="toggle_on" />
                        </IconAction>
                      )}
                      {/* Cash is protected (delete disabled); card methods derive from a
                          connection and are offered NO destructive delete; only custom
                          external methods can be removed. */}
                      {!isCard &&
                        (isCash ? (
                          <Tooltip title="Cash can’t be removed">
                            <span>
                              <IconButton
                                size="small"
                                aria-label={`Delete ${method.label}`}
                                disabled
                              >
                                <Icon name="delete" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : (
                          <IconAction
                            size="small"
                            label={`Delete ${method.label}`}
                            onClick={() => void handleDelete(method)}
                          >
                            <Icon name="delete" />
                          </IconAction>
                        ))}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {methods.status === 'ready' && sorted.length > 0 && visibleRows.length === 0 && (
        <ListNoMatch noun="payment methods" query={search} />
      )}

      <PaymentMethodDialog
        open={dialog !== null}
        method={dialog?.method ?? null}
        onClose={() => setDialog(null)}
        onSaved={() => {
          setDialog(null);
          refetch();
        }}
        showToast={showToast}
      />
    </Box>
  );
}
